import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useId, useRef, useState } from "react";
import { CheckIcon } from "./Icons.js";
import { MascotArt } from "../art/mascot.js";
import { useReducedMotion } from "../hooks.js";
import { IDENTITY_COLOURS, cssVar, identityColour, initialsFor } from "../tokens.js";
/**
 * **The fallback ladder has two rungs and both of them must be reachable** —
 * S2 §3.1 amendment 1 (LOCKED), made true by Ivy's §22.4 ruling.
 *
 * 1. the assigned preset's art, on its own bed, never tinted or overlaid;
 * 2. art absent, failed or unknown → a **two-letter monogram from the
 *    nickname** on the `hash(players.id)` bed.
 *
 * Rung 2 was unreachable: this component's own contract said *"falls back to
 * initials on empty **or on error**"* and only the empty branch existed, so any
 * load failure painted the hash bed with an empty `<img>` on it — pixel for
 * pixel, a plain coloured disc. **There is no state in any spec that renders
 * one**, which is why §22.4 calls that a defect rather than a fallback.
 *
 * The failure is remembered **by URL** rather than as a boolean, so a new `url`
 * (the picker's swap, a conversion, a fresh profile read) gets its own attempt
 * instead of inheriting the previous one's verdict. One `useState`, no effect,
 * and nothing to reset.
 *
 * A guest is not a special case here or anywhere else (§22.4, and its non-goal
 * is explicit): a guest gets preset art identical to a registered player's, and
 * the only thing they lack is the picker.
 */
/**
 * **The monogram's size, as a ratio of the avatar's diameter.**
 *
 * Exported because §23.2.1's size table specifies the lobby seat row's monogram
 * as *"the component's own ratio (~10 at 28, ~12 at 34)"* rather than as two
 * numbers. `SeatPips` reads this constant, so the pip and the `Avatar` it
 * becomes when the wire field lands (L3) cannot disagree — and nobody has to
 * re-derive 10 and 12 from a comment. Moving this value moves both.
 */
export const MONOGRAM_RATIO = 0.36;
export function Avatar({ nickname, initials, url, size = 40, hero, className, ...bed }) {
    const useHero = hero ?? size >= 48;
    const [failedUrl, setFailedUrl] = useState(null);
    const showArt = typeof url === "string" && url.length > 0 && url !== failedUrl;
    /**
     * One rotation, reached two ways — a hash of a real identity, or an index the
     * server already assigned. Both land on the same six accents, which is what
     * keeps a seat's bed and its ring from being two colour vocabularies.
     */
    const bedToken = bed.playerId === undefined
        ? (IDENTITY_COLOURS[bed.colorIndex % IDENTITY_COLOURS.length] ?? "play")
        : identityColour(bed.playerId);
    const style = {
        width: size,
        height: size,
        background: cssVar(bedToken),
        fontSize: Math.round(size * MONOGRAM_RATIO),
    };
    return (_jsx("span", { className: ["wr-avatar", useHero ? "wr-avatar--hero" : "", className ?? ""]
            .filter(Boolean)
            .join(" "), style: style, "aria-hidden": "true", "data-avatar-rung": showArt ? "art" : "initials", children: showArt ? (_jsx("img", { className: "wr-avatar__img", src: url, alt: "", width: size, height: size, 
            /**
             * **Ivy §23.2.1, in the tile's single `<button>`: a thumb starting on
             * a face must not begin an image drag and eat the press.** The
             * attribute is here rather than at that one call site because it
             * cannot be set from CSS, and because it is true of every avatar in
             * the product: this image is decorative (`alt=""`, the wrapper is
             * `aria-hidden`) and dragging one has never been an affordance.
             * The other half of the rule — `pointer-events: none` — *is* a CSS
             * property and is scoped to the tile, in `lobby.css`.
             */
            draggable: false, onError: () => setFailedUrl(url) })) : ((initials ?? initialsFor(nickname))) }));
}
/**
 * A frame that names the art it is waiting for (§16).
 *
 * Ships the fallback in every environment — the slot id is metadata, not a
 * dev-only placeholder chrome, because a visible "ART GOES HERE" box in
 * staging is the thing that ends up in a screenshot to Aaron.
 */
export function ArtSlot({ slot, size, children, className, style }) {
    return (_jsx("span", { className: ["wr-art-slot", className ?? ""].filter(Boolean).join(" "), "data-art-slot": slot, style: { width: size, height: size, ...style }, children: children }));
}
/**
 * ART-SLOT `wr-mascot` — Wren's art, from S3.
 *
 * The S2 fallback (a `W` in Archivo Black on gold) is **gone**, not kept behind
 * a flag: the art exists, it is inlined, and a fallback for an asset that has
 * shipped is a second rendering nobody will ever look at again. §16's binding
 * constraint — survive the 28px top-bar chip — is what the `-chip` optical
 * variant exists for, and `MascotArt` picks it by size.
 *
 * Which drawing this is, and how to change it, lives in `../art/mascot.tsx`.
 * Nothing here names a take or a file.
 */
export function Mascot({ size, hero = true, className }) {
    return (_jsx(ArtSlot, { slot: "wr-mascot", size: size, className: className, children: _jsx("span", { className: ["wr-mascot", hero ? "wr-mascot--hero" : ""].filter(Boolean).join(" "), style: { width: size, height: size }, "aria-hidden": "true", children: _jsx(MascotArt, { size: size }) }) }));
}
const LOCKUP = {
    sm: { mascot: 28, type: "wr-display-xs" },
    md: { mascot: 40, type: "wr-display-sm" },
    lg: { mascot: 56, type: "wr-display-md" },
};
/**
 * Mascot + stacked WORDY / ROYALE (§4).
 *
 * **This component is still non-interactive**: it renders no link, no button
 * and no tab stop, and it never will. What changed at S11 is the *lock around
 * it*, not the component — Ivy lobby §23.3 amends §10 amendment 1 so the lockup
 * may be **wrapped** in a router `Link` on non-lobby screens, and the wrapper is
 * the interactive element. Pass `decorative` there so the link owns the one
 * accessible name.
 *
 * PRD §5.1's "the lockup is not a home button" survives intact and literally: a
 * button that navigates is a link wearing a costume, and this is a link.
 */
export function BrandLockup({ size = "md", orientation = "inline", wordy, royale, appName, showMascot = true, className, decorative = false, }) {
    const spec = LOCKUP[size];
    return (_jsxs("span", { className: [
            "wr-lockup",
            orientation === "stacked" ? "wr-lockup--stacked" : "",
            className ?? "",
        ]
            .filter(Boolean)
            .join(" "), ...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": appName }), children: [showMascot ? _jsx(Mascot, { size: spec.mascot, hero: size !== "sm" }) : null, _jsxs("span", { className: "wr-lockup__words", "aria-hidden": "true", children: [_jsx("span", { className: spec.type, children: wordy }), _jsx("span", { className: `${spec.type} wr-lockup__royale`, children: royale })] })] }));
}
export function RoleChip({ label }) {
    return _jsx("span", { className: "wr-role-chip wr-mono-sm", children: label });
}
/**
 * A nickname inside fixed-width chrome — design spec §14.1 **ruling B, LOCKED:
 * one line, always.**
 *
 * The flyout's name column is ~166px (268 − 36 pad − 54 avatar − 12 gap).
 * Archivo Black is wide, nicknames are legal to 20 characters (§9), and two
 * lines of 22px display type shove the `RoleChip` down and unbalance the
 * header. So, in Ivy's order:
 *
 * 1. **A deterministic size step by character count, with no JS measurement.**
 *    `≤12 → display-md`, `≥13 → display-sm`. Measured against the real column
 *    at 22px (~13.6px per character ⇒ 12 characters ≈ 164px), so her cut point
 *    stands and is used unchanged.
 * 2. **An ellipsis backstop, always applied** — including at `display-sm`,
 *    where a 20-character name still clips. That is accepted here and only
 *    here. `min-width: 0` goes on this element *and* its flex parent, because
 *    a flex child will not shrink below its content without it and the
 *    `text-overflow` would then never fire.
 * 3. **The full nickname is never lost — a requirement on the SURFACE,
 *    satisfied ONCE (Ivy R-K4, S14).** `title` always carries it whole; the
 *    *accessible* half is carried by **whichever element actually exposes a
 *    name** — this span under `nameExposure="self"`, or an ancestor under
 *    `"ancestor"`. **A second copy inside a subtree an ancestor's own name
 *    overrides does not satisfy the rule twice; it satisfies it zero additional
 *    times.** The header row also leads to the profile screen, where the name
 *    renders untruncated: chrome may abbreviate a player's name; their own
 *    profile screen may not.
 *
 * The drawer passes `base="display-sm"`: its column is ~190px, which holds all
 * 20 legal characters at 17px, so the step has nowhere further to go — and the
 * ramp has no rung below `display-sm` that Ivy authorised for a name. The
 * ellipsis and the full-name attributes still apply; rule 4 puts the drawer
 * under this rule and those are the parts of it that bind there.
 *
 * It does **not** apply to §9 `/welcome`, where the nickname is the subject of
 * the screen and has the full width — that screen does not use this component.
 */
export function ChromeNickname({ nickname, base = "display-md", nameExposure, }) {
    const stepped = base === "display-md" && nickname.length >= 13 ? "display-sm" : base;
    /**
     * **`role="group"` + `aria-label`, or NEITHER — S14 `A5`, Dex Addendum 8.**
     *
     * `A1` (S13, Addendum 3) added `role="group"` here because the `aria-label`
     * sat on a role-less `<span>` whose implicit `generic` role is
     * **name-prohibited**, so the one thing this element exists to guarantee was
     * not reliably exposed. `group` fits the container in every place this
     * component renders: none is a list, a table or a grid, so `listitem` and
     * `row` are out, and `img` is rejected for R.3's reason at the score cell —
     * it announces "graphic", and a player's name is not one.
     *
     * **What `A5` changed:** that pair is now emitted **only** where this span is
     * the surface's exposure. Under `"ancestor"` the pair is not merely
     * unnecessary, it is *unreachable* — an ancestor whose own name overrides its
     * subtree (an `aria-label`-bearing `<button>`, whose ARIA children are
     * presentational besides) means neither attribute can ever be spoken. Both go:
     * the label because it is inert, and the role because a **nameless `group`**
     * is a bare accessibility-tree node some readers announce as a group boundary.
     *
     * **The spread is deliberate and is the shipped precedent** (`Lobby.tsx`'s
     * composed tile, T-552's own no-op classification): role and label move
     * **together, as one inseparable object**, so no future edit can reintroduce
     * one without the other.
     */
    const exposure = nameExposure === "self" ? { role: "group", "aria-label": nickname } : {};
    return (_jsx("span", { className: `wr-${stepped} wr-chrome-nickname`, 
        /**
         * **R-K2 (Ivy, LOCKED): `title` is emitted in BOTH modes.** It is not
         * dead under `"ancestor"` — `title` renders a browser tooltip on hover
         * regardless of ARIA, so it serves **sighted pointer users reading a
         * truncated nickname**, a different channel with a different audience.
         * A deletion that took both would remove a working affordance on the
         * evidence of a broken one.
         */
        title: nickname, ...exposure, "data-size": stepped, children: nickname }));
}
/**
 * `Intl.NumberFormat` grouping, with the §9.2 abbreviation above 99,999.
 *
 * The pill never exceeds 92px, and `1,240,000` at `mono-md` does not fit in it.
 * Abbreviating is not hiding the number — the flyout's `CoinPlate` and the
 * profile screen show it in full; chrome may abbreviate, a player's own wallet
 * screen may not (the same rule `ChromeNickname` follows for names).
 */
export function formatCoins(value) {
    if (Math.abs(value) <= 99_999)
        return value.toLocaleString();
    const thousands = value / 1000;
    return `${(Math.round(thousands * 10) / 10).toLocaleString()}k`;
}
/**
 * **§9.4's balance-change motion, and the three ways it is suppressed.**
 *
 * A value change cross-fades and rises 4px (`--wr-dur`, 180ms); on an
 * **increase** the coin dot also pulses once. Never a rolling odometer — a
 * scramble delays a number people want to read.
 *
 * It is suppressed, and each suppression is a rule rather than a nicety:
 *
 *  1. **`prefers-reduced-motion`** — instant swap, no pulse (§9.4, S2 §16).
 *  2. **The change was not witnessed** (`allowed === false`): the boot seed and
 *     the post-reconnect reconcile land as instant swaps. §20.4: *"the motion is
 *     for a change the player witnessed; a reconcile is a catch-up, and
 *     animating it would stage an outage's worth of movement as a credit that
 *     just happened."*
 *  3. **The first value of all.** A chip that fades its own arrival in is
 *     animating a change nobody made.
 *
 * The pulse is on **increases only** — a debit is not a celebration — which is
 * why this returns three values and not a boolean.
 */
function useCoinMotion(value, allowed) {
    const reducedMotion = useReducedMotion();
    const previous = useRef(undefined);
    const before = previous.current;
    previous.current = value;
    if (reducedMotion || !allowed)
        return "none";
    // `undefined` is "nothing has rendered yet" — distinct from a `null` balance.
    if (before === undefined || before === value)
        return "none";
    if (before !== null && value !== null && value > before)
        return "credit";
    return "change";
}
/** The §9.2 progression, derived — never passed in, so it cannot disagree. */
export function coinChipState(value, shopEnabled) {
    if (value === null)
        return "stub";
    return shopEnabled ? "live" : "live-inert";
}
/**
 * The coin chip, in all three of Ivy's §9.2 states.
 *
 * The progression is LOCKED and each step adds exactly one thing:
 *
 *  - `stub` — em-dash, `aria-hidden`, not focusable, not tappable, no `+`.
 *  - `live-inert` — a real number, still not focusable, still no `+`. This is
 *    the state a shopless S4 ships in, and it is the one that is easiest to get
 *    wrong: a chip showing a balance that *looks* pressable and does nothing is
 *    worse than one that never looked pressable.
 *  - `live` — the whole pill navigates to the Shop, with the `+` as decoration
 *    inside that single target (S2 §10 amendment 3 — never a nested control).
 *
 * **In S3 only `stub` renders**, because `viewer.balanceCoins` is `null` until
 * S4-1. The other two are built and covered by tests rather than deferred; the
 * switch between them is one nullable field and one flag.
 */
export function CoinChip({ value, shopEnabled = false, onClick, label, liveLabel, liveDescription, dimmed = false, animate = false, }) {
    const state = coinChipState(value, shopEnabled);
    const motion = useCoinMotion(value, animate && !dimmed);
    const descriptionId = useId();
    const className = `wr-coin-chip${dimmed ? " wr-coin-chip--dim" : ""}`;
    const content = (_jsxs(_Fragment, { children: [_jsx("span", { className: `wr-coin-chip__dot${motion === "credit" ? " wr-coin-chip__dot--pulse" : ""}`, "data-art-slot": "wr-coin", "aria-hidden": "true" }), _jsx("span", { className: `wr-mono-md${motion === "none" ? "" : " wr-coin-chip__value--change"}`, "data-testid": "coin-chip-value", children: value === null ? "—" : formatCoins(value) }, motion === "none" ? "static" : String(value)), state === "live" ? (_jsx("span", { className: "wr-coin-chip__plus", "aria-hidden": "true", children: "+" })) : null] }));
    if (state === "live") {
        /**
         * **A fragment, not one element (Ivy §9.2 addendum (b)).** The description
         * span is a **sibling after** the button. It is `wr-visually-hidden`, so it
         * takes no layout in the top bar's flex row.
         *
         * **Accepted deviation, named by Ivy so QA does not file it:** a screen
         * reader may say the number twice on a credit — once because the accessible
         * *name* changed, once because the live region read its own content.
         * *"Information beats silence; a name without the balance is the worse
         * failure."*
         */
        return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: className, "data-testid": "coin-chip", "data-state": state, "data-dimmed": dimmed ? "true" : undefined, onClick: onClick, "aria-label": liveLabel, "aria-describedby": descriptionId, "aria-live": "polite", children: content }), _jsx("span", { id: descriptionId, className: "wr-visually-hidden", children: liveDescription })] }));
    }
    return (_jsx("span", { className: className, "data-testid": "coin-chip", "data-state": state, "data-dimmed": dimmed ? "true" : undefined, "data-stub": state === "stub" ? "true" : undefined, "aria-hidden": state === "stub" ? "true" : undefined, "aria-label": state === "stub" ? undefined : label, "aria-live": "polite", role: state === "stub" ? undefined : "status", children: content }));
}
/**
 * The coin plate — the flyout's (§12) and the profile screen's (§5.1).
 *
 * **One component, two sizes, and the same number.** Ivy §5.1 is LOCKED that
 * the profile renders *the same `balanceCoins` value the top-bar chip renders*
 * and that the profile endpoint returns no second balance; §15 rule 3 repeats
 * it as a rule Rafi may not improvise around. The plate therefore takes the
 * value as a prop from the one provider and has no way to fetch one.
 *
 * Same stub rule as the chip: `—` is *"no balance source exists"*, `0` is a
 * real, empty wallet, and neither is ever substituted for the other (§9.2).
 * Unlike the chip this never abbreviates — chrome may shorten a number, a
 * player's own wallet screen may not (`formatCoins`' own docblock).
 */
export function CoinPlate({ value, label, eyebrow, size = "md", dimmed = false, animate = false, testId, }) {
    const motion = useCoinMotion(value, animate && !dimmed);
    const dot = (_jsx("span", { className: `wr-coin-chip__dot${motion === "credit" ? " wr-coin-chip__dot--pulse" : ""}`, "data-art-slot": "wr-coin", "aria-hidden": "true" }));
    /**
     * Keyed by the value so a change **remounts** the numeral, which fires the
     * cross-fade + 4px rise once per change with no timer to clear — the same
     * mechanism the chip uses, so the two surfaces animate identically or not at
     * all.
     */
    const numeral = (_jsx("span", { className: `wr-mono-${size}${motion === "none" ? "" : " wr-coin-chip__value--change"}`, "data-testid": "coin-plate-value", children: value === null ? "—" : value.toLocaleString() }, motion === "none" ? "static" : String(value)));
    const className = [
        "wr-coin-plate",
        eyebrow ? "wr-coin-plate--stacked" : "",
        dimmed ? "wr-coin-plate--dim" : "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsx("div", { className: className, "data-testid": testId ?? "coin-plate", "data-stub": value === null || undefined, "data-dimmed": dimmed ? "true" : undefined, children: eyebrow ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "wr-mono-xs wr-coin-plate__eyebrow", children: eyebrow }), _jsxs("span", { className: "wr-row", style: { gap: "var(--wr-s-2)" }, children: [dot, numeral] })] })) : (_jsxs(_Fragment, { children: [dot, numeral, _jsx("span", { className: "wr-visually-hidden", children: label })] })) }));
}
/**
 * **The avatar picker is a RADIOGROUP — S2 §22.13, one shared component, both
 * screens.**
 *
 * S6 §10 rule 6 said the profile picker *"reuses `/welcome`'s
 * `role="radiogroup"` semantics"*; `/welcome` never had them, so a verbatim
 * reuse inherited `role="group"` + `aria-pressed`. Ivy: *"my rule was right
 * about the destination and wrong about the tense."* Fixed once, here, so
 * `/welcome` and `/profile` change together — **divergence between the two
 * pickers is the one outcome neither spec permits.**
 *
 * ── Why radiogroup rather than ratifying what shipped ───────────────────────
 *
 *  - `aria-pressed` is a **toggle**: it states that each face is independently
 *    on or off, which is a false description of a set where choosing one
 *    un-chooses another. Nothing announced position in the set and nothing
 *    announced the un-selection of the previous choice.
 *  - **Eighteen tab stops**, on the mandatory onboarding gate, for a keyboard
 *    or switch user crossing to Save. A radiogroup is one. *"This is the cost
 *    that decides it."*
 *  - **Selection here is a draft** — nothing commits until Save (§4.2) or
 *    Continue (§9) — which makes APG's selection-follows-focus *safe*: an arrow
 *    key may move and select in one gesture because the move has no side
 *    effect. The textbook case for the pattern, not the compromise case.
 *
 * ── What is unchanged, and is LOCKED ────────────────────────────────────────
 *
 * 56px circles, 12px gap, snap-scroll row / 6-up desktop grid, the 64×69 hit
 * areas (already clear of §22.12's floor), the check badge, `.wr-scroll`, both
 * commit models, and the guest branches — **the picker never renders for a
 * guest** (replaced on `/welcome`, no editor on `/profile`), so no
 * disabled-radio case exists. The visual selection treatment is byte-identical;
 * only the attribute the CSS keys off moved (`aria-pressed` → `aria-checked`).
 */
export function AvatarPicker({ options, selectedId, onSelect, labelledBy, loading = false, disabled = false, }) {
    const rowRef = useRef(null);
    const reducedMotion = useReducedMotion();
    if (loading) {
        /**
         * §22.13.1 rule 10 — **loading skeletons are not radios.** The container
         * carries `aria-busy` and contains zero `role="radio"` children until the
         * set lands; a radiogroup of eight pulsing spans would announce a choice
         * that does not exist yet.
         *
         * **`role="group"` — S14 `A4`, the site `A1` filed.** The name arrives by
         * `aria-labelledby` (the visible "Choose your look" eyebrow) on an element
         * whose implicit `generic` role is **name-prohibited**, so the loaded
         * branch's `radiogroup` was named and this one was not — the same field,
         * silent for exactly as long as the fetch takes.
         *
         * **Why `group` fits THIS container.** Both call sites wrap the picker in a
         * `<div className="wr-stack">` (`IdentityCard`'s editor and `/welcome`), so
         * there is no list, table or grid for `listitem`/`row`/`gridcell` to belong
         * to, and no `<button>` ancestor to make the node presentational.
         * **`status` is rejected**, the same rejection `A1` banked at
         * `LobbySkeleton`: its implicit `aria-live="polite"` would make this an
         * *announcing* region, which is a behaviour change — `aria-busy` is already
         * the non-announcing signal this branch wants. **`progressbar` is rejected**
         * for making a claim about progress that nothing here measures.
         */
        return (_jsx("div", { className: "wr-avatar-picker wr-scroll", "aria-busy": "true", role: "group", "aria-labelledby": labelledBy, children: Array.from({ length: 8 }, (_, index) => (_jsx("span", { className: "wr-pulse", style: {
                    width: 56,
                    height: 56,
                    borderRadius: "var(--wr-r-pill)",
                    background: "var(--wr-muted)",
                    flex: "none",
                    animation: `wrPulse 900ms var(--wr-ease-in) ${index * 60}ms infinite alternate`,
                } }, index))) }));
    }
    const checkedIndex = options.findIndex((option) => option.id === selectedId);
    /**
     * **Roving tabindex — exactly one tab stop** (rule 3). `0` on the checked
     * option, `-1` on every other. With nothing checked — only reachable if the
     * player's face is not in the pool — `0` on the first option, the same
     * fallback the `?edit=avatar` focus effect already used.
     */
    const tabStopIndex = checkedIndex >= 0 ? checkedIndex : 0;
    /** Move focus **and** selection together (rule 4), then follow with the row. */
    const moveTo = (index) => {
        const option = options[index];
        if (!option)
            return;
        onSelect(option);
        const node = rowRef.current?.querySelectorAll('[role="radio"]')[index];
        node?.focus();
        /**
         * Rule 5 — `"nearest"` on **both** axes. `inline` alone lets the page jump
         * vertically on a picker sitting mid-card. Rule 6: `smooth`, and `auto`
         * under `prefers-reduced-motion` — this is the one place in the card where
         * motion is *navigation* rather than decoration.
         */
        node?.scrollIntoView?.({
            block: "nearest",
            inline: "nearest",
            behavior: reducedMotion ? "auto" : "smooth",
        });
    };
    const onKeyDown = (event, index) => {
        const last = options.length - 1;
        if (last < 0)
            return;
        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                event.preventDefault();
                moveTo(index === last ? 0 : index + 1); // wraps at both ends (rule 4)
                return;
            case "ArrowLeft":
            case "ArrowUp":
                event.preventDefault();
                moveTo(index === 0 ? last : index - 1);
                return;
            case "Home":
                event.preventDefault();
                moveTo(0);
                return;
            case "End":
                event.preventDefault();
                moveTo(last);
                return;
            case " ":
            case "Spacebar":
                // `Enter` is deliberately NOT bound here — it belongs to the form.
                event.preventDefault();
                moveTo(index);
                return;
            default:
                return;
        }
    };
    return (_jsx("div", { ref: rowRef, className: "wr-avatar-picker wr-scroll", role: "radiogroup", "aria-labelledby": labelledBy, children: options.map((option, index) => (_jsxs("button", { type: "button", className: "wr-avatar-picker__option", role: "radio", "aria-checked": option.id === selectedId, "aria-label": option.name, 
            /**
             * Rule 9 — `submitting` keeps every option `disabled`. A fully
             * disabled radiogroup has no tab stop for that interval, which is
             * correct: there is nothing to choose while the form is in flight.
             */
            disabled: disabled, tabIndex: index === tabStopIndex ? 0 : -1, "data-testid": "avatar-option", onClick: () => onSelect(option), onKeyDown: (event) => onKeyDown(event, index), children: [_jsx(Avatar, { playerId: option.id, nickname: option.name, url: option.url, size: 56, hero: false }), option.id === selectedId ? (_jsx("span", { className: "wr-avatar-picker__badge", "aria-hidden": "true", children: _jsx(CheckIcon, { size: 11 }) })) : null] }, option.id))) }));
}
//# sourceMappingURL=Identity.js.map